import { useQuery } from "@apollo/client";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { StaffCersRejectionFindingsQuery } from "generated-graphql/graphql";
import { extractOmnisearchValue, useOmnisearchState } from "hooks/omnisearch-v2";
import { GET_STAFF_CERS_REJECTION_FINDINGS } from "hooks/useStaffCersRejectionFindings";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { StatusMetricCard } from "../StatusMetricCard";

const SECTION_LABEL_SX = {
  mb: 1.5,
  display: "block",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontSize: "0.7rem",
} as const;

type Stats = NonNullable<StaffCersRejectionFindingsQuery["staffCersRejectionFindings"]["stats"]>;

/** One card per omnisearch bucket the backend registers for the grid. */
const CARD_DEFS: {
  section: "Findings" | "Aging";
  name: string;
  key: string;
  value: string;
  stat: (stats: Stats) => number;
}[] = [
  {
    section: "Findings",
    name: "Unresolved",
    key: "status",
    value: "UNRESOLVED",
    stat: (s) => s.unresolved,
  },
  {
    section: "Findings",
    name: "Out of Compliance >30d",
    key: "oocOver30",
    value: "true",
    stat: (s) => s.oocOver30,
  },
  { section: "Findings", name: "Pending", key: "status", value: "PENDING", stat: (s) => s.pending },
  {
    section: "Findings",
    name: "Resolved This Month",
    key: "resolvedThisMonth",
    value: "true",
    stat: (s) => s.resolvedThisMonth,
  },
  {
    section: "Aging",
    name: "30–60 Days",
    key: "ageBucket",
    value: "30_60",
    stat: (s) => s.age30to60,
  },
  {
    section: "Aging",
    name: "60–90 Days",
    key: "ageBucket",
    value: "60_90",
    stat: (s) => s.age60to90,
  },
  {
    section: "Aging",
    name: "90+ Days",
    key: "ageBucket",
    value: "90_plus",
    stat: (s) => s.age90plus,
  },
];

/**
 * One titled cluster of stat cards (local copy of the gen-status layout).
 * Section labels are neutral on purpose — per UX, the blue accent is reserved
 * for ACTIVE filter cards, so per-section status colors came out.
 */
function StatSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={SECTION_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
        {children}
      </Box>
    </Box>
  );
}

function SectionDivider() {
  return <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", lg: "flex" } }} />;
}

/**
 * Dashboard cards above the rejections grid, using the shared CERS
 * StatusMetricCard (same look/behavior as the Facilities matching cards, per
 * UX). Reads stats from a lightweight companion query (same operation,
 * pageSize 1) scoped to the same omnisearch, so counts always match the
 * filtered grid. Clicking a card resets to just that bucket so the grid
 * matches the card's count; clicking the active card again clears the bucket.
 * `csm` is preserved either way, so a CSM with "My Findings" on stays in
 * their personal scope (their card counts are already csm-scoped; wiping csm
 * would drop them to the global set and break count-integrity).
 */
export function RejectionStatCards() {
  const { omnisearch, createUrlFromOmnisearchKeyValues } = useOmnisearchState();
  const navigate = useNavigate();

  const { data, loading } = useQuery(GET_STAFF_CERS_REJECTION_FINDINGS, {
    variables: { search: omnisearch, page: 0, pageSize: 1 },
    fetchPolicy: "cache-and-network",
  });
  const stats = data?.staffCersRejectionFindings.stats;

  const cardHref = (key: string, value: string) =>
    createUrlFromOmnisearchKeyValues({ keyValues: [[key, value]], keysToKeep: ["csm"] });

  const isSelected = (key: string, value: string) =>
    extractOmnisearchValue(omnisearch, key) === value;

  const toggleCard = (key: string, value: string) => {
    const href = isSelected(key, value)
      ? createUrlFromOmnisearchKeyValues({ keyValues: [], keysToKeep: ["csm"] })
      : cardHref(key, value);
    // createUrlFromOmnisearchKeyValues returns an ABSOLUTE url (protocol +
    // host); navigate() would treat that as a relative path and mangle it,
    // so reduce it to pathname + search first.
    const parsed = new URL(href, window.location.origin);
    navigate(`${parsed.pathname}${parsed.search}`, { preventScrollReset: true });
  };

  const section = (label: "Findings" | "Aging") => (
    <StatSection label={label}>
      {CARD_DEFS.filter((c) => c.section === label).map((card) => (
        <StatusMetricCard
          key={`${card.key}:${card.value}`}
          name={card.name}
          value={stats ? card.stat(stats) : undefined}
          loading={loading}
          selected={isSelected(card.key, card.value)}
          onClick={() => toggleCard(card.key, card.value)}
        />
      ))}
    </StatSection>
  );

  return (
    <Paper elevation={0} sx={{ px: 3, py: 2.5 }}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={3} sx={{ minWidth: 0 }}>
        {section("Findings")}
        <SectionDivider />
        {section("Aging")}
      </Stack>
    </Paper>
  );
}
